import { GitBranch, Network, PanelRightClose, PanelRightOpen } from 'lucide-react'
import { useStudioStore } from '@/store/studio-store'
import { cn } from '@/lib/utils'
import { useStudioLocale } from '@/i18n'
import { getStudioAuthorityTaskCopy } from '@/i18n/studio-authority-copy'
import { COOGO_IDE_CHROME_COPY } from './coogo-ide-chrome-copy'
import { getSelectionTitle, useStudioSelectionContext } from './selection-context'
import { useStudioProjectOutputKind } from "./components/artifact-mode-switcher"
import { activateStudioAuthorityTask } from "./studio-authority-activation"
import { getStudioAuthorityTasks } from "./studio-authority-workspaces"

export function SelectionCommandBar({
  className,
  compact = false,
  showSummary = true,
  detailsOpen,
  onToggleDetails,
}: {
  className?: string
  compact?: boolean
  showSummary?: boolean
  detailsOpen?: boolean
  onToggleDetails?: () => void
}) {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const outputKind = useStudioProjectOutputKind()
  const selection = useStudioSelectionContext()
  const setResourcesSelection = useStudioStore((s) => s.setResourcesSelection)
  const openResourcesDrawer = useStudioStore((s) => s.openResourcesDrawer)
  const setInspectorPanelOpen = useStudioStore((s) => s.setInspectorPanelOpen)

  const primaryTasks = getStudioAuthorityTasks("primary", outputKind)
  const screenTask = primaryTasks.find((task) => task.railItem === "editor")
  const behaviorTask = primaryTasks.find((task) => task.railItem === "behavior")
  const screenCopy = screenTask ? getStudioAuthorityTaskCopy(locale, "primary", outputKind, screenTask) : null
  const behaviorCopy = behaviorTask ? getStudioAuthorityTaskCopy(locale, "primary", outputKind, behaviorTask) : null

  const openResources = () => {
    openResourcesDrawer(selection)
  }

  const openBehavior = () => {
    setResourcesSelection(selection)
    activateStudioAuthorityTask({
      domain: "primary",
      outputKind,
      railItem: 'behavior',
    })
  }

  const buttonClass = cn(
    'inline-flex items-center gap-1.5 rounded-md border border-border bg-background font-medium text-foreground hover:bg-muted',
    compact ? 'h-7 px-2' : 'h-8 px-2.5',
  )

  return (
    <div className={cn('flex min-w-0 items-center text-xs', compact ? 'flex-nowrap gap-1.5' : 'flex-wrap gap-2', className)}>
      {showSummary && (
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <span className="shrink-0 font-semibold text-foreground">{getSelectionTitle(selection)}</span>
            <span className="min-w-0 truncate font-mono text-muted-foreground">
              {selection
                ? `${selection.label === 'workspace' ? copy.workspace : selection.label} · ${selection.detail}`
                : copy.selectSomething}
            </span>
          </div>
        </div>
      )}
      {onToggleDetails && (
        <button
          type="button"
          onClick={onToggleDetails}
          aria-label={screenCopy?.label ?? "Screen"}
          className={buttonClass}
        >
          {detailsOpen ? <PanelRightClose className="h-3.5 w-3.5" /> : <PanelRightOpen className="h-3.5 w-3.5" />}
          {screenCopy?.label ?? "Screen"}
        </button>
      )}
      <button type="button" onClick={openResources} aria-label={copy.details} className={buttonClass}>
        <Network className="h-3.5 w-3.5" />
        {copy.details}
      </button>
      {behaviorCopy && (
        <button
        type="button"
        onClick={openBehavior}
        aria-label={behaviorCopy.label} className={buttonClass}
      >
        <GitBranch className="h-3.5 w-3.5" />
          {behaviorCopy.label}
        </button>
      )}
    </div>
  )
}
